import { v4 as uuidv4 } from "uuid";

import { ELogLevel } from "../../web-shared-components/helpers/logger/ILogger";
/**
 * Generates a uuidv4
 *
 * @returns - the uuidv4
 */
export function generateGUID(): string {
	return uuidv4().split("-").join("");
}

/**
 * Encode plain string to base64 (function as MDN suggests)
 *
 * @param str - the string to encode
 * @returns - a base64 encoded string
 */
export function b64EncodeUnicode(str: string): string {
	return btoa(
		encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
			return String.fromCharCode(parseInt(p1, 16));
		})
	);
}

/**
 * Decode from base64 (function as MDN suggests)
 *
 * @param str - the string to decode
 * @returns - a decoded string
 */
function b64DecodeUnicode(str: string): string {
	return decodeURIComponent(
		Array.prototype.map
			.call(atob(str), (c: string) => {
				return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join("")
	);
}

/**
 * Convert the presenceState into a readable string
 *
 * @param presence - the presence state to check
 * @returns - readable string
 */
function iPresenceStateToString(presence: number): string {
	/*
		 CONTACT_STATE_UNCONTROLLED 0x00000000 // Kontakt ohne Präsenzinformation
		 CONTACT_STATE_AVAILABLE    0x00000001
		 CONTACT_STATE_BUSY         0x00000002
		 CONTACT_STATE_ABSENT       0x00000003
		 CONTACT_STATE_DONOTDISTURB 0x00000004
		 CONTACT_STATE_OUTOFORDER   0x00000007 // nur für schwarze Icons, die es für Kontakte eigentlich nicht gibt. Wird im Client benötigt aber nicht auf der Schnittstelle.
		 */
	let presenceString;
	switch (presence & 0x000000ff) {
		case 0x00000001:
			presenceString = "AVAILABLE";
			break;
		case 0x00000002:
			presenceString = "BUSY";
			break;
		case 0x00000003:
			presenceString = "ABSENT";
			break;
		case 0x00000004:
			presenceString = "DND";
			break;
		case 0x00000007:
			presenceString = "OUTOFORDER";
			break;
		default: // LC was: UNCONTROLLED
			presenceString = "UNAVAILABLE";
			break;
	}

	const flags = presence & 0xffff0000;
	/*
	 CONTACT_STATE_RINGING              0x01000000 //!> Eine Leitung des Kontaktes klingelt
	 CONTACT_STATE_APPOINTMENT          0x02000000 //!> Der Kontakt hat einen Termin
	 CONTACT_STATE_CALL                 0x04000000 //!> Der Kontakt ist im Gespräch
	 CONTACT_STATE_OFFLINE              0x08000000 //!> Der Kontakt ist nicht am Server angemeldet
	 CONTACT_STATE_NOTESET              0x10000000 //!> Der Kontakt hat eine Notiz gesetzt
	 CONTACT_STATE_IDLE                 0x20000000 //!> Der Kontakt ist Idle
	 CONTACT_STATE_MOBILEAVAILABILITY   0x40000000 //!> FIVE-311: Der Benutzer ist wahrscheinlicher mobil erreichbar
	 */
	const additionalStates = [];
	if (flags & 0x01000000) additionalStates.push("RINGING");

	if (flags & 0x02000000) additionalStates.push("APPOINTMENT");

	if (flags & 0x04000000) additionalStates.push("CALL");

	if (flags & 0x08000000) additionalStates.push("OFFLINE");

	if (flags & 0x10000000) additionalStates.push("NOTESET");

	if (flags & 0x20000000) additionalStates.push("IDLE");

	if (flags & 0x40000000) additionalStates.push("MOBILEAVAILABILITY");

	// Falls Offline angegeben ist -> zeige grau an
	if (additionalStates.indexOf("OFFLINE") > -1) presenceString = "UNAVAILABLE";
	// LC was: OFFLINE
	// Ansonsten normale Präsenz zurückgeben
	else if (additionalStates.indexOf("RINGING") > -1) presenceString = "RINGING";

	// to match with the UserPresenceIcon logic.
	// However, it would be better to keep them uppercase for the localization labels.
	return presenceString.toLowerCase();
}

/**
 * Add the sip prefix if it does not exist in the contact id
 * @param contactID - the contact ID to check
 * @returns - the correct sip
 */
export function addSipPrefixIfNotExists(contactID?: string): string {
	if (!contactID) return "";

	const sip = contactID.substring(0, 4);
	if (sip === "sip:") return contactID;

	return "sip:" + contactID;
}

/**
 * Get the LogLevel Enum from a string
 * @param logLevel - the loglevel as a string
 * @returns - the enum
 */
export const getELogLevelFromString = (logLevel: string): ELogLevel => {
	switch (logLevel) {
		case "error":
			return ELogLevel.ERROR;
		case "warn":
			return ELogLevel.WARN;
		case "info":
			return ELogLevel.INFO;
		case "debug":
			return ELogLevel.DEBUG;
		default:
			return ELogLevel.DEBUG;
	}
};
